import { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  VOICE_EXECUTION_DEFAULTS,
  VoiceExecutionMessageType,
  voiceExecutionConfigSchema,
  type VoiceExecutionConfig,
} from '@reach/shared-types/tool-voice-execution';
import { VOICE_BEHAVIOR_COPY as copy } from './voice-behavior-copy';
import formStyles from './ToolForm.module.css';
import styles from './VoiceBehaviorSection.module.css';

interface VoiceBehaviorSectionProps {
  value?: VoiceExecutionConfig | null;
  onChange: (value: VoiceExecutionConfig | null) => void;
}

/** Shared Voice controls for HTTP and schema-lookup tool authoring. */
export function VoiceBehaviorSection({ value, onChange }: VoiceBehaviorSectionProps) {
  const id = useId();
  const config = { ...VOICE_EXECUTION_DEFAULTS, ...value };
  const messageIsStatic = config.executionMessageType === VoiceExecutionMessageType.STATIC_TEXT;
  const voiceConfigValid = voiceExecutionConfigSchema.nullish().safeParse(value).success;
  const hint = messageIsStatic ? copy.staticHint : copy.promptHint;
  const messageId = `${id}-message`;
  const typeId = `${id}-type`;
  const hintId = `${id}-hint`;
  const safetyHintId = `${id}-safety-hint`;
  const errorId = `${id}-error`;
  const messageDescription = voiceConfigValid
    ? `${hintId} ${safetyHintId}`
    : `${hintId} ${safetyHintId} ${errorId}`;
  const set = <K extends keyof VoiceExecutionConfig>(key: K, updatedValue: VoiceExecutionConfig[K]) =>
    onChange({ ...value, [key]: updatedValue });

  return (
    <div>
      <details className={formStyles.section}>
        <summary className={styles.summary}>
          <span className={styles.summaryText}>
            <span className={styles.title}>{copy.title}</span>
            <span className={styles.subtitle}>{copy.subtitle}</span>
          </span>
          <ChevronDown className={styles.chevron} aria-hidden="true" />
        </summary>
        <div className={styles.body}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={config.speakDuringExecution}
              onChange={event => set('speakDuringExecution', event.target.checked)}
            />
            {copy.waiting}
          </label>
          {config.speakDuringExecution && (
            <>
              <div className={styles.messageField}>
                <label htmlFor={typeId}>{copy.messageType}</label>
                <select
                  id={typeId}
                  value={config.executionMessageType}
                  className={formStyles.select}
                  onChange={event =>
                    set('executionMessageType', event.target.value as VoiceExecutionMessageType)
                  }>
                  <option value={VoiceExecutionMessageType.PROMPT}>{copy.prompt}</option>
                  <option value={VoiceExecutionMessageType.STATIC_TEXT}>{copy.staticText}</option>
                </select>
              </div>
              <div className={styles.messageField}>
                <label htmlFor={messageId}>{copy.message}</label>
                <p id={hintId} className={formStyles.helpText}>
                  {hint}
                </p>
                <textarea
                  id={messageId}
                  value={config.executionMessage ?? ''}
                  className={formStyles.textarea}
                  rows={3}
                  aria-describedby={messageDescription}
                  aria-invalid={!voiceConfigValid}
                  onChange={event => set('executionMessage', event.target.value)}
                />
                <p id={safetyHintId} className={formStyles.helpText}>
                  {copy.safetyHint}
                </p>
              </div>
            </>
          )}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={config.speakAfterExecution}
              onChange={event => set('speakAfterExecution', event.target.checked)}
            />
            {copy.completion}
          </label>
          {!config.speakAfterExecution && (
            <p role="status" className={formStyles.helpText}>
              {copy.completionWarning}
            </p>
          )}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={config.enableTypingSound}
              onChange={event => set('enableTypingSound', event.target.checked)}
            />
            {copy.typing}
          </label>
          <p className={formStyles.helpText}>{copy.defaults}</p>
          <button type="button" className={styles.reset} onClick={() => onChange(null)}>
            {copy.reset}
          </button>
        </div>
      </details>
      {!voiceConfigValid && (
        <p id={errorId} role="alert" className={formStyles.fieldError}>
          {copy.emptyStatic}
        </p>
      )}
    </div>
  );
}
